import { ReactNode, useCallback, useMemo, useState } from 'react'
import { createStrictContext } from '../../../context'
import { TargetCandidate } from '../../../../domain/entity/DiscussionEntity'
import { VersionReportVersionEntity } from '../../../../domain/entity/version-report/VersionReportVersionEntity'
import { UseDiscussionPanelReturnValue } from '../hooks/useDiscussionPanel'

type DiscussionActionContextValue = {
  open: () => void
  close: () => void
  toggle: () => void
  startNewThread: () => void
  closeNewThreadForm: () => void
  startTargetDiscussion: (target: TargetCandidate) => void
  clearPendingTarget: () => void
  setPendingTarget: (target: TargetCandidate) => void
  setTargetCandidates: (candidates: TargetCandidate[]) => void
  setSelectedTargetUuid: (uuid: string | null) => void
  openFilterOverlay: () => void
  closeFilterOverlay: () => void
}

type DiscussionStateContextValue = {
  isOpen: boolean
  pendingTarget: TargetCandidate | null
  versionPageUuid: string | undefined
  currentVersionUuid: string | undefined
  templatePageUuid: string | undefined
  versionList: VersionReportVersionEntity[]
  targetCandidates: TargetCandidate[]
  selectedTargetUuid: string | null
  showNewThreadForm: boolean
  isFilterOverlayOpen: boolean
}

const [useDiscussionActionContext, DiscussionActionProvider] =
  createStrictContext<DiscussionActionContextValue>()
const [useDiscussionStateContext, DiscussionStateProvider] =
  createStrictContext<DiscussionStateContextValue>()

export { useDiscussionActionContext, useDiscussionStateContext }

type DiscussionProviderProps = {
  discussionPanel: UseDiscussionPanelReturnValue
  versionPageUuid: string | undefined
  currentVersionUuid: string | undefined
  templatePageUuid: string | undefined
  versionList: VersionReportVersionEntity[]
  children: ReactNode
}

export const DiscussionProvider = ({
  discussionPanel,
  versionPageUuid,
  currentVersionUuid,
  templatePageUuid,
  versionList,
  children,
}: DiscussionProviderProps) => {
  const { isOpen, open, close: closePanel, toggle } = discussionPanel
  const [pendingTarget, setPendingTarget] = useState<TargetCandidate | null>(
    null
  )
  const [targetCandidates, setTargetCandidates] = useState<TargetCandidate[]>(
    []
  )

  const [selectedTargetByPage, setSelectedTargetByPage] = useState<
    Map<string, string>
  >(new Map())
  const [showNewThreadForm, setShowNewThreadForm] = useState(false)
  const [isFilterOverlayOpen, setIsFilterOverlayOpen] = useState(false)

  const selectedTargetUuid = versionPageUuid
    ? (selectedTargetByPage.get(versionPageUuid) ?? null)
    : null

  const setSelectedTargetUuid = useCallback(
    (uuid: string | null) => {
      if (!versionPageUuid) return
      setSelectedTargetByPage(prev => {
        const next = new Map(prev)
        if (uuid === null) {
          next.delete(versionPageUuid)
        } else {
          next.set(versionPageUuid, uuid)
        }
        return next
      })
    },
    [versionPageUuid]
  )

  const openFilterOverlay = useCallback(() => setIsFilterOverlayOpen(true), [])
  const closeFilterOverlay = useCallback(
    () => setIsFilterOverlayOpen(false),
    []
  )

  const clearPendingTarget = useCallback(() => {
    setPendingTarget(null)
  }, [])

  const closeNewThreadForm = useCallback(() => {
    setShowNewThreadForm(false)
    setPendingTarget(null)
  }, [])

  const close = useCallback(() => {
    closePanel()
    setPendingTarget(null)
    setShowNewThreadForm(false)
  }, [closePanel])

  const startTargetDiscussion = useCallback(
    (target: TargetCandidate) => {
      setPendingTarget(target)
      setShowNewThreadForm(true)
      open()
    },
    [open]
  )

  const startNewThread = useCallback(() => {
    if (selectedTargetUuid) {
      const target = targetCandidates.find(c => c.uuid === selectedTargetUuid)
      if (target) {
        setPendingTarget(target)
      }
    }
    setShowNewThreadForm(true)
  }, [selectedTargetUuid, targetCandidates])

  const actionValue = useMemo<DiscussionActionContextValue>(
    () => ({
      open,
      close,
      toggle,
      startTargetDiscussion,
      startNewThread,
      closeNewThreadForm,
      clearPendingTarget,
      setPendingTarget,
      setTargetCandidates,
      setSelectedTargetUuid,
      openFilterOverlay,
      closeFilterOverlay,
    }),
    [
      open,
      close,
      toggle,
      startTargetDiscussion,
      startNewThread,
      closeNewThreadForm,
      clearPendingTarget,
      setSelectedTargetUuid,
      openFilterOverlay,
      closeFilterOverlay,
    ]
  )

  const stateValue = useMemo<DiscussionStateContextValue>(
    () => ({
      isOpen,
      pendingTarget,
      versionPageUuid,
      currentVersionUuid,
      templatePageUuid,
      versionList,
      targetCandidates,
      selectedTargetUuid,
      showNewThreadForm,
      isFilterOverlayOpen,
    }),
    [
      isOpen,
      pendingTarget,
      versionPageUuid,
      currentVersionUuid,
      templatePageUuid,
      versionList,
      targetCandidates,
      selectedTargetUuid,
      showNewThreadForm,
      isFilterOverlayOpen,
    ]
  )

  return (
    <DiscussionActionProvider value={actionValue}>
      <DiscussionStateProvider value={stateValue}>
        {children}
      </DiscussionStateProvider>
    </DiscussionActionProvider>
  )
}
